import type { Identifier, XYCoord } from "dnd-core";
import type { FC } from "react";
import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";

import { Box } from "@mui/material";
import React from "react";
import { draggableItemTypes } from "../../shared/data";

export interface CardProps {
  id: any;
  index: number;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  onDrop: () => void;
  children: any;
  isSelected?: boolean;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export const DraggableItem: FC<CardProps> = React.memo((props) => {
  const { id, index, moveCard, children, onDrop, ...restProps } = props;
  const ref = useRef<HTMLDivElement>(null);
  // const dragHandleRef = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: draggableItemTypes.CARD,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
    drop(item: DragItem, monitor) {
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex !== hoverIndex) {
        onDrop();
      }
    },
  });

  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: draggableItemTypes.CARD,
    item: () => {
      return { id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const opacity = isDragging ? 1 : 1;
  drag(drop(ref));

  return (
    <>
      <Box
        sx={{
          opacity: opacity,
          padding: "4px",
          // backgroundColor: restProps?.isSelected ? "#ced4da" : "#fff",
        }}
        className={restProps?.isSelected ? "draggable-list-item" : ""}
        ref={preview}
      >
        <Box ref={ref} data-handler-id={handlerId}>
          {children}
        </Box>
      </Box>
    </>
  );
});
